import type { RequirementFields } from "../types";

// Offsite-agnostische User-Prompt-Builder. Format ist für alle Offsites
// gleich; nur die System-Prompts (Rolle/Kontext) variieren pro Offsite.

export function buildEvaluationUserPrompt(
  topicTitle: string,
  teamName: string | null,
  fields: RequirementFields,
): string {
  const teamLabel = teamName ? `Team „${teamName}"` : `Team`;
  return `--- USER INPUT ---
${teamLabel} hat zum Thema „${topicTitle}" folgende Anforderung formuliert:

Was soll gebaut werden: ${fields.was}
Für wen: ${fields.fuerWen}
Welches Problem löst es: ${fields.problem}
Was muss es können: ${fields.anforderungen}
Was darf es NICHT: ${fields.einschraenkungen}
Beispiel-Szenario: ${fields.beispiel}
--- END USER INPUT ---`;
}

export function buildOvernightUserPrompt(fields: RequirementFields): string {
  return `--- USER INPUT ---
Hier ist die Anforderung:

Was soll gebaut werden: ${fields.was}
Für wen: ${fields.fuerWen}
Welches Problem löst es: ${fields.problem}
Was muss es können: ${fields.anforderungen}
Was darf es NICHT: ${fields.einschraenkungen}
Beispiel-Szenario: ${fields.beispiel}
--- END USER INPUT ---`;
}

/**
 * Prompt-Builder für Kurzformulare (AR-Session, drei Felder).
 * Leere Felder werden weggelassen statt als leeres Label ausgegeben —
 * ein Label ohne Inhalt liest das Modell als "hier steht nichts Wichtiges".
 */
export function buildKurzOvernightUserPrompt(
  fields: RequirementFields,
): string {
  const lines: string[] = [];
  const add = (label: string, value: string | undefined) => {
    const v = (value ?? "").trim();
    if (v) lines.push(`${label} ${v}`);
  };
  add("Thema:", fields.thema);
  add("Was gebaut werden soll:", fields.was);
  add("Worauf es besonders ankommt:", fields.anforderungen);
  return `--- USER INPUT ---
${lines.join("\n")}
--- END USER INPUT ---`;
}

// ---------- Reserve (Notfall-Modus) ----------
//
// Der Notfall-Modus zeigt vorbereitete Prototypen, wenn WLAN oder Anthropic-API
// vor dem Gremium ausfallen. Vorbereitet hieß bisher: Ergebnisse eines früheren
// Durchlaufs, also zu einem fremden Thema. Auf der Leinwand wäre das aufgefallen,
// sobald die Runde ein eigenes Rahmenthema gesetzt hat.
//
// Deshalb baut die Generierungs-Lambda die Reserve neu, sobald das Rahmenthema
// feststeht — sechs Prototypen zum selben Thema, aus sechs verschiedenen
// Blickwinkeln. Sechs, weil die Sitzung mit bis zu sechs Prompts geplant ist und
// gleich aussehende Reiter den Notfall verraten würden.

/** Ein Blickwinkel je Reserve-Prototyp: Kurztitel und die Anforderung dazu. */
export interface ReserveBlickwinkel {
  /** Steht als Zusatz im Titel über der Karte. */
  titel: string;
  /** „Was gebaut werden soll" — in der Sprache eines Gremienmitglieds. */
  was: string;
  /** „Worauf es besonders ankommt". */
  anforderungen: string;
}

export const RESERVE_BLICKWINKEL: readonly ReserveBlickwinkel[] = [
  {
    titel: "Überblick",
    was: "Eine Übersichtsseite, die den aktuellen Stand zum Thema auf einen Blick zeigt",
    anforderungen:
      "Auf einem großen Bildschirm aus der Entfernung lesbar; die wichtigste Zahl steht vorn",
  },
  {
    titel: "Erfassen",
    was: "Ein einfaches Formular, mit dem ein Anliegen zum Thema gemeldet und erfasst wird",
    anforderungen:
      "Wenige Felder, klare Beschriftung, nach dem Absenden eine sichtbare Bestätigung",
  },
  {
    titel: "Status verfolgen",
    was: "Eine Seite, auf der man den Bearbeitungsstand eines Vorgangs zum Thema verfolgt",
    anforderungen:
      "Der Weg vom Eingang bis zum Abschluss ist als Abfolge sichtbar, mit Datum je Schritt",
  },
  {
    titel: "Auswertung",
    was: "Eine Auswertung, die zeigt, wie sich das Thema über die Zeit entwickelt hat",
    anforderungen:
      "Verlauf und Vergleich zum Vorjahr; ohne Fachbegriffe verständlich",
  },
  {
    titel: "Nachschlagen",
    was: "Eine Suche, mit der man zum Thema schnell die passende Auskunft findet",
    anforderungen:
      "Sofortige Treffer beim Tippen, Antwort in zwei Sätzen, Herkunft der Auskunft genannt",
  },
  {
    titel: "Erinnern",
    was: "Eine Ansicht, die an offene Fristen und Termine zum Thema erinnert",
    anforderungen:
      "Fällig, bald fällig und erledigt sind auseinanderzuhalten; das Dringendste steht oben",
  },
];

/**
 * Anforderung für einen Reserve-Prototyp — Rahmenthema plus Blickwinkel.
 *
 * Bewusst dieselbe Form wie eine echte Teilnehmereingabe: So läuft die Reserve
 * durch denselben Prompt-Bauer und denselben System-Prompt wie der Ernstfall
 * und sieht am Ende auch so aus.
 */
export function buildReserveFields(
  thema: string,
  blickwinkel: ReserveBlickwinkel,
): RequirementFields {
  return {
    thema: thema.trim(),
    was: blickwinkel.was,
    fuerWen: "",
    problem: "",
    anforderungen: blickwinkel.anforderungen,
    einschraenkungen: "",
    beispiel: "",
  };
}
